import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import { installEarlyErrorCapture, scheduleSentry } from './lib/errorReporting'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AppRouter } from './router/index'
import { preloadRoute } from './router/preload'
import { AuthProvider } from './contexts/AuthContext'

// Sentry is loaded after the window load event (see lib/errorReporting.ts).
// Until then, uncaught errors are caught by a tiny listener and forwarded
// once Sentry is up, so nothing in the first second is lost.
installEarlyErrorCapture()
scheduleSentry()

// Register service worker -install prompt only shown in /portal routes (handled by CoachScottWidget)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Service worker registration failed silently
    })
  })
}

async function bootstrap(): Promise<void> {
  // Public routes are prerendered to HTML at build time, so the user is
  // already looking at the page while this runs. Loading the route chunk
  // first lets the initial render commit the full page in one pass instead of
  // flashing the Suspense spinner over the prerendered content. A failure
  // here is not fatal: lazyWithRetry's normal path (and its chunk recovery)
  // takes over inside the render.
  // Let the web fonts land before React's first render. The LCP element on
  // every public page is a heading set in Cormorant Garamond; Chrome only
  // counts it once it repaints in the web font, and that repaint was queuing
  // behind React's initial render on a throttled CPU (measured ~1.8 s later
  // than the font arrival). Capped so a stalled font never blocks the app.
  await Promise.race([
    document.fonts.ready,
    new Promise<void>((resolve) => window.setTimeout(resolve, 2500)),
  ])

  try {
    await preloadRoute(window.location.pathname)
  } catch {
    // handled by lazyWithRetry during render
  }

  createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
      <StrictMode>
        <HelmetProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </HelmetProvider>
      </StrictMode>
    </ErrorBoundary>,
  )
}

void bootstrap()
